import LocalStorage from '@/utils/local-storage';
import {
    getConfigUrl,
    castValue as castValueByType,
    mergeConfigs,
    ValueType,
} from '@/utils/config';

export const AppConfig = {
    VITE_ENGINE_URL: ValueType.String,
    VITE_VIINEX_URL: ValueType.String,
    VITE_WEBRTC_STUN_SERVER_URL: ValueType.String,
} as const;

type AppConfigType = Record<string, string | number | boolean | undefined>;

type TypedConfigProperties = {
    [K in keyof typeof AppConfig]: (typeof AppConfig)[K] extends typeof ValueType.Number
        ? number
        : (typeof AppConfig)[K] extends typeof ValueType.Boolean
          ? boolean
          : string;
};

const CONFIG_STORAGE_KEY = 'app_config';

class Config {
    private config: AppConfigType = {} as AppConfigType;
    private initialized = false;

    public async init(): Promise<void> {
        if (this.initialized) {
            return;
        }

        this.loadFromCache();
        await this.loadFromServer();
    }

    private loadFromCache(): void {
        const cachedConfig =
            LocalStorage.get<AppConfigType>(CONFIG_STORAGE_KEY);
        if (cachedConfig) {
            this.config = cachedConfig;
            this.setProperties();
            this.initialized = true;
        }
    }

    private async loadFromServer(): Promise<void> {
        const configUrl = getConfigUrl();

        try {
            const serverConfig = await this.fetchConfig(configUrl);
            this.updateConfig(serverConfig);
        } catch (error) {
            console.error(`Error loading config file`, error);
        }
    }

    private async fetchConfig(url: string): Promise<Record<string, unknown>> {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(
                `Failed to load config: ${url} (${response.status})`,
            );
        }

        return response.json();
    }

    private updateConfig(serverConfig: Record<string, unknown>): void {
        this.config = mergeConfigs(this.config, serverConfig) as AppConfigType;
        LocalStorage.set(CONFIG_STORAGE_KEY, this.config);
        this.setProperties();
        this.initialized = true;
    }

    private setProperties(): void {
        const typeAssertions: Record<
            ValueType,
            (value: string | number | boolean) => string | number | boolean
        > = {
            [ValueType.Number]: (value) => value as number,
            [ValueType.Boolean]: (value) => value as boolean,
            [ValueType.String]: (value) => value as string,
        };

        Object.keys(this.config).forEach((key) => {
            if (key in AppConfig) {
                const configKey = key as keyof typeof AppConfig;
                const valueType = AppConfig[configKey] as ValueType;

                Object.defineProperty(this, key, {
                    configurable: true,
                    get: () =>
                        typeAssertions[valueType](
                            this.castValue(configKey, this.config[key]),
                        ),
                });
            } else {
                Object.defineProperty(this, key, {
                    configurable: true,
                    get: () => this.config[key],
                });
            }
        });
    }

    public getAll(): AppConfigType {
        return { ...this.config };
    }

    public isReady(): boolean {
        return this.initialized;
    }

    private castValue(
        key: keyof typeof AppConfig,
        value: unknown,
    ): string | number | boolean {
        const valueType = AppConfig[key] as ValueType;
        return castValueByType(value, valueType);
    }

    public get(key: string, defaultValue?: unknown): unknown {
        const rawValue = this.config[key] ?? defaultValue;

        if (key in AppConfig) {
            return this.castValue(key as keyof typeof AppConfig, rawValue);
        }

        return String(rawValue ?? '');
    }
}

type ConfigInstance = Config & TypedConfigProperties & AppConfigType;

const configInstance = new Config() as ConfigInstance;

if (import.meta.hot) {
    if (import.meta.hot.data?.configState) {
        const state = import.meta.hot.data.configState;
        (configInstance as unknown as { config: AppConfigType })['config'] =
            state.config;
        (configInstance as unknown as { initialized: boolean })['initialized'] =
            state.initialized;
        if (state.initialized) {
            (configInstance as unknown as { setProperties: () => void })[
                'setProperties'
            ]();
        }
    }
    import.meta.hot.dispose((data) => {
        data.configState = {
            config: configInstance.getAll(),
            initialized: configInstance.isReady(),
        };
    });
}

export default configInstance;
